import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PayPalWrapper from '../components/PayPalWrapper';
import { getCurrentUser, UserResponse, createMPSubscription } from '../services/api';

type PaymentMethod = 'paypal' | 'mercadopago' | null;

const PricingPage = () => {
  const [user, setUser] = useState<UserResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [mpLoading, setMpLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>(null);
  const navigate = useNavigate();

  const handleSuccess = (planName: string) => {
    setSuccess(planName);
    setTimeout(() => {
      navigate('/dashboard');
    }, 5000);
  };

  const handleMPPayment = async (plan: string) => {
    try {
      setMpLoading(true);
      const res = await createMPSubscription(plan);
      if (res && res.init_point) {
        window.location.href = res.init_point;
      } else {
        alert('Hubo un error al iniciar MercadoPago. Intentá de nuevo.');
        setMpLoading(false);
      }
    } catch (err) {
      console.error(err);
      alert('Hubo un error al iniciar MercadoPago.');
      setMpLoading(false);
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const currentUser = await getCurrentUser();
        if (currentUser) setUser(currentUser);
      } catch (err: any) {
        console.log('Not logged in or error:', err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  if (loading) {
    return (
      <div className="light-theme-page min-vh-100 d-flex align-items-center justify-content-center">
        <p className="text-center">Cargando...</p>
      </div>
    );
  }

  return (
    <div className="light-theme-page">
      <div className="container py-5">

        {/* Header */}
        <div className="text-center mb-4">
          <h2 className="fw-bolder">Mejora tu Plan</h2>
          <p className="lead fw-normal text-muted mb-3">
            Elige el plan que mejor se adapte a tus necesidades.
          </p>
        </div>

        {/* Payment method selector */}
        {user && (
          <div className="row justify-content-center mb-4">
            <div className="col-lg-8">
              <div className="p-4 rounded-3 bg-light border shadow-sm">
                <p className="fw-semibold text-center mb-3">
                  <i className="bi bi-credit-card-fill text-primary me-2"></i>
                  ¿Con qué método querés abonar?
                </p>
                <div className="d-flex justify-content-center gap-3 flex-wrap">
                  <button
                    className={`btn btn-lg fw-bold px-4 ${selectedMethod === 'paypal' ? 'btn-warning' : 'btn-outline-warning'}`}
                    onClick={() => setSelectedMethod('paypal')}
                  >
                    💳 PayPal / Tarjeta Internacional
                  </button>
                  <button
                    className={`btn btn-lg fw-bold px-4 ${selectedMethod === 'mercadopago' ? 'btn-primary' : 'btn-outline-primary'}`}
                    style={selectedMethod === 'mercadopago' ? { backgroundColor: '#009ee3', borderColor: '#009ee3' } : { borderColor: '#009ee3', color: '#009ee3' }}
                    onClick={() => setSelectedMethod('mercadopago')}
                  >
                    🇦🇷 MercadoPago (ARS)
                  </button>
                </div>

                {/* Contextual info note */}
                {selectedMethod === 'paypal' && (
                  <p className="mb-0 mt-3 text-muted small text-center">
                    <i className="bi bi-info-circle-fill text-primary me-1"></i>
                    Podés pagar con <strong>tarjeta de crédito o débito</strong> a través de PayPal{' '}
                    <u>sin necesidad de tener una cuenta</u>. Seleccioná <strong>"Pagar con tarjeta"</strong> al abrirse la ventana.
                  </p>
                )}
                {selectedMethod === 'mercadopago' && (
                  <p className="mb-0 mt-3 text-muted small text-center">
                    <i className="bi bi-info-circle-fill text-primary me-1"></i>
                    Serás redirigido a <strong>MercadoPago</strong> para completar el pago en pesos argentinos.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Success banner */}
        {success && (
          <div
            className="alert alert-success border-0 shadow-lg position-fixed top-0 start-50 translate-middle-x mt-4 py-3 px-5 animate__animated animate__zoomIn"
            style={{ zIndex: 2000, borderRadius: '15px', background: 'linear-gradient(135deg, #28a745, #218838)', color: 'white', minWidth: '350px' }}
          >
            <div className="d-flex align-items-center">
              <i className="bi bi-check-circle-fill fs-1 me-4"></i>
              <div>
                <h4 className="mb-1 fw-bold">¡Suscripción Activada!</h4>
                <p className="mb-0 fs-5">Tu plan ahora es <strong>{success.toUpperCase()}</strong>.</p>
                <small className="opacity-75">Redirigiendo al panel en unos segundos...</small>
              </div>
            </div>
          </div>
        )}

        {/* Plan cards */}
        <div className="row gx-5 justify-content-center">

          {/* Plan Medio */}
          <div className="col-lg-6 col-xl-4 mb-4">
            <div className="card h-100 border-0" style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
              <div className="card-body p-5 d-flex flex-column">
                <div className="mb-3">
                  <h3 className="fw-bold text-dark display-5 mb-0">Medio</h3>
                </div>
                <div className="mb-3">
                  <span className="display-4 fw-bold">$5</span>
                  <span className="text-muted"> / mes</span>
                  <span className="d-block text-muted small">ARS 7.500 / mes con MercadoPago</span>
                </div>
                <ul className="list-unstyled mb-4">
                  <li className="mb-2"><i className="bi bi-check text-primary"></i> Acceso web y Telegram</li>
                  <li className="mb-2"><i className="bi bi-check text-primary"></i> Recibe recordatorios</li>
                  <li className="mb-2"><i className="bi bi-check text-primary"></i> Nombre de asistente personalizable</li>
                  <li className="mb-2"><i className="bi bi-check text-primary"></i> Hasta 35 mensajes por mes</li>
                  <li className="mb-2 text-muted"><i className="bi bi-x"></i> Sin personalización</li>
                </ul>
                <div className="mt-auto d-grid gap-2">
                  {user ? (
                    <>
                      {!selectedMethod && (
                        <p className="text-muted small text-center mb-0">
                          ↑ Seleccioná un método de pago arriba para continuar
                        </p>
                      )}
                      {selectedMethod === 'paypal' && (
                        <PayPalWrapper plan="medio" onSuccess={handleSuccess} />
                      )}
                      {selectedMethod === 'mercadopago' && (
                        <button
                          className="btn text-white fw-bold"
                          style={{ backgroundColor: '#009ee3' }}
                          onClick={() => handleMPPayment('MEDIO')}
                          disabled={mpLoading}
                        >
                          {mpLoading ? (
                            <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Redirigiendo...</>
                          ) : (
                            '🇦🇷 Pagar con MercadoPago (ARS 7.500)'
                          )}
                        </button>
                      )}
                    </>
                  ) : (
                    <Link to="/register?plan=MEDIO&redirect=/pricing" className="btn btn-primary">
                      Regístrate para adquirir
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Plan Full */}
          <div className="col-lg-6 col-xl-4 mb-4">
            <div className="card h-100 border-0" style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
              <div className="card-body p-5 d-flex flex-column">
                <div className="mb-3">
                  <h3 className="fw-bold text-dark display-5 mb-0">Full</h3>
                </div>
                <div className="mb-3">
                  <span className="display-4 fw-bold">$10</span>
                  <span className="text-muted"> / mes</span>
                  <span className="d-block text-muted small">ARS 15.000 / mes con MercadoPago</span>
                </div>
                <ul className="list-unstyled mb-4">
                  <li className="mb-2"><i className="bi bi-check text-primary"></i> Acceso Web y Telegram</li>
                  <li className="mb-2"><i className="bi bi-check text-primary"></i> Mensajes Ilimitados</li>
                  <li className="mb-2"><i className="bi bi-check text-primary"></i> Nombre de Asistente Personalizable</li>
                  <li className="mb-2"><i className="bi bi-check text-primary"></i> Recibe recordatorios</li>
                  <li className="mb-2"><i className="bi bi-check text-primary"></i> <strong>Personalización y entrenamiento IA</strong></li>
                  <li className="mb-2"><i className="bi bi-check text-primary"></i> Soporte Prioritario</li>
                </ul>
                <div className="mt-auto d-grid gap-2">
                  {user ? (
                    <>
                      {!selectedMethod && (
                        <p className="text-muted small text-center mb-0">
                          ↑ Seleccioná un método de pago arriba para continuar
                        </p>
                      )}
                      {selectedMethod === 'paypal' && (
                        <PayPalWrapper plan="full" onSuccess={handleSuccess} />
                      )}
                      {selectedMethod === 'mercadopago' && (
                        <button
                          className="btn text-white fw-bold"
                          style={{ backgroundColor: '#009ee3' }}
                          onClick={() => handleMPPayment('FULL')}
                          disabled={mpLoading}
                        >
                          {mpLoading ? (
                            <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Redirigiendo...</>
                          ) : (
                            '🇦🇷 Pagar con MercadoPago (ARS 15.000)'
                          )}
                        </button>
                      )}
                    </>
                  ) : (
                    <Link to="/register?plan=FULL&redirect=/pricing" className="btn btn-primary">
                      Regístrate para adquirir
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PricingPage;